import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * ShortcutsHelp — overlay con tutte le scorciatoie da tastiera, attivato da "?".
 *
 * Pattern Linear/Notion: l'utente preme "?" (senza modifiers, fuori da input)
 * → si apre un dialog modale read-only. ESC o click fuori chiude.
 *
 * Self-installs il listener globale, non serve alcuna prop.
 */
const SHORTCUTS = [
  { keys: ['⇧', 'N'], label: 'Apri Quick Add (MIT / azione)' },
  { keys: ['⌘', 'K'], label: 'Apri Command Palette / ricerca' },
  { keys: ['['], label: 'Periodo precedente (giorno / settimana / mese)' },
  { keys: [']'], label: 'Periodo successivo' },
  { keys: ['T'], label: 'Vai a Oggi' },
  { keys: ['?'], label: 'Mostra questa guida' },
  { keys: ['Esc'], label: 'Chiudi modali / overlay' },
];

export default function ShortcutsHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)
        || e.target?.isContentEditable;
      if (isInput) return;
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="shortcuts-help-overlay"
    >
      <div
        className="w-full max-w-md mx-4 rounded-md bg-paper shadow-xl border border-line"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Scorciatoie da tastiera"
        data-testid="shortcuts-help-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-linesoft">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-soft">
            Scorciatoie
          </h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-mute hover:text-ink"
            aria-label="Chiudi"
            data-testid="shortcuts-help-close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        <ul className="px-5 py-5 space-y-3">
          {SHORTCUTS.map((s) => (
            <li key={s.label} className="flex items-center justify-between gap-4">
              <span className="font-body text-sm text-ink">{s.label}</span>
              <span className="flex items-center gap-1.5">
                {s.keys.map((k) => (
                  <kbd
                    key={k}
                    className="font-mono text-[11px] px-2 py-0.5 border border-line bg-linesoft rounded shrink-0"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <div className="px-5 py-3 border-t border-linesoft bg-canvas rounded-b-md">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
            Premi <kbd className="font-mono text-[10px] px-1.5 py-0.5 border border-line bg-paper rounded">?</kbd> in qualsiasi momento
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
